/**
 * Health Data Export - CSV export button with date range picker
 * Downloads health aggregate data for coaching analysis
 */

import React, { useState } from 'react';
import { Button, Alert } from './ui';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';

const HealthDataExport: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(ninetyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Make API call to export endpoint
      const response = await apiClient.get(
        `/api/daily-health-aggregates/export_csv/?start_date=${startDate}&end_date=${endDate}`,
        {
          responseType: 'blob' // Important for downloading files
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `health_data_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: SPACING[24],
      backgroundColor: COLORS.bg.panel,
      borderRadius: RADIUS,
      border: `1px solid ${COLORS.border.default}`
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING[12],
        marginBottom: SPACING[24]
      }}>
        <ArrowDownTrayIcon style={{ width: 20, height: 20, color: COLORS.text.secondary }} />
        <h3 style={{
          margin: 0,
          fontSize: TYPOGRAPHY.size.lg,
          fontWeight: TYPOGRAPHY.weight.semibold,
          color: COLORS.text.primary
        }}>
          Export Health Data
        </h3>
      </div>

      <p style={{
        margin: `0 0 ${SPACING[24]}`,
        fontSize: TYPOGRAPHY.size.sm,
        color: COLORS.text.secondary,
        lineHeight: 1.5
      }}>
        Download your health metrics as a CSV file for analysis, coaching, or record keeping.
        Includes sleep, HRV, activity, and training data with baselines and deltas.
      </p>

      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {/* Date Range */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: SPACING[16],
        marginBottom: SPACING[24]
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
            marginBottom: SPACING[8]
          }}>
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate}
            style={{
              width: '100%',
              padding: SPACING[12],
              backgroundColor: COLORS.bg.base,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              color: COLORS.text.primary,
              fontSize: TYPOGRAPHY.size.sm
            }}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
            marginBottom: SPACING[8]
          }}>
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={today}
            style={{
              width: '100%',
              padding: SPACING[12],
              backgroundColor: COLORS.bg.base,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              color: COLORS.text.primary,
              fontSize: TYPOGRAPHY.size.sm
            }}
          />
        </div>
      </div>

      {/* Quick presets */}
      <div style={{
        display: 'flex',
        gap: SPACING[8],
        marginBottom: SPACING[24],
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => {
            const date = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setStartDate(date);
            setEndDate(today);
          }}
          style={{
            padding: `${SPACING[4]} ${SPACING[12]}`,
            fontSize: TYPOGRAPHY.size.xs,
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            color: COLORS.text.secondary,
            cursor: 'pointer'
          }}
        >
          Last 30 days
        </button>
        <button
          type="button"
          onClick={() => {
            const date = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setStartDate(date);
            setEndDate(today);
          }}
          style={{
            padding: `${SPACING[4]} ${SPACING[12]}`,
            fontSize: TYPOGRAPHY.size.xs,
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            color: COLORS.text.secondary,
            cursor: 'pointer'
          }}
        >
          Last 90 days
        </button>
        <button
          type="button"
          onClick={() => {
            const date = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setStartDate(date);
            setEndDate(today);
          }}
          style={{
            padding: `${SPACING[4]} ${SPACING[12]}`,
            fontSize: TYPOGRAPHY.size.xs,
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            color: COLORS.text.secondary,
            cursor: 'pointer'
          }}
        >
          Last year
        </button>
      </div>

      <Button
        onClick={handleExport}
        disabled={loading || !startDate || !endDate}
        style={{ width: '100%' }}
      >
        <ArrowDownTrayIcon style={{ width: 18, height: 18, marginRight: 8 }} />
        {loading ? 'Exporting...' : 'Download CSV'}
      </Button>

      <p style={{
        margin: `${SPACING[12]} 0 0`,
        fontSize: TYPOGRAPHY.size.xs,
        color: COLORS.text.tertiary,
        textAlign: 'center'
      }}>
        CSV includes: Sleep, HRV, RHR, Steps, Training Zones, Body Metrics, Baselines, Deltas
      </p>
    </div>
  );
};

export default HealthDataExport;
